import ApiResponse from '@/api/android/interfaces/ApiResponse';
import AppTreeAdLogRequest from '@/api/android/interfaces/AppTreeAdLogRequest';
import AppTreeAdResponse from '@/api/android/interfaces/AppTreeAdResponse';
import BridgeBaseRequest from '@/api/android/interfaces/BridgeBaseRequest';
import BridgeBaseResponse from '@/api/android/interfaces/BridgeBaseResponse';
import CustomerCallRequest from '@/api/android/interfaces/CustomerCallRequest';
import DeviceStatusResponse from '@/api/android/interfaces/DeviceStatusResponse';
import GetOrderDataResponse from '@/api/android/interfaces/GetOrderDataResponse';
import LimitMenuResponse from '@/api/android/interfaces/LimitMenuResponse';
import PaymentApprovalRequest from '@/api/android/interfaces/PaymentApprovalRequest';
import PaymentApprovalResponse from '@/api/android/interfaces/PaymentApprovalResponse';
import QrLoginResponse from '@/api/android/interfaces/QrLoginResponse';
import ServiceOrderRequest from '@/api/android/interfaces/ServiceOrderRequest';
import SetOrderDataRequest from '@/api/android/interfaces/SetOrderDataRequest';
import SetOrderDataResponse from '@/api/android/interfaces/SetOrderDataResponse';
import { LocaleLanguage } from '@/enums/LocaleLanguage';
import UserError from '@/error';
import i18n from '@/i18n';
import { logger } from '@/logger';
import { useConfigStore } from '@/stores/config';
import { localeToPosLanguage } from '@/utils';

declare global {
  interface Window {
    AndroidBridge: any;
  }
}

const lastBridgeRequestIds: Record<string, number> = {};

/**
 * 설정 데이터에서 Android 요청 기본 정보 생성
 * @returns Android 호출 시 전달하는 기본 정보
 */
const getBridgeBaseRequest = (): BridgeBaseRequest => {
  const config = useConfigStore().config;
  return {
    POSApiURL: config.POS_API_URL || '',
    CompanyID: config.StoreID,
    StoreCode: config.SubStoreCode,
    PosID: config.PosCode || '',
    TabCode: config.TableCode || '',
    Lang: localeToPosLanguage(i18n.global.locale.value as LocaleLanguage),
  };
};

/**
 * 안드로이드 브릿지 함수 호출 (기본 요청 데이터 포함)
 * @param bridgeName - AndroidBridge에서 호출할 메서드 명
 * @param data - 전달할 데이터
 */
const callBridgeWithBaseAsync = async <TResponse>(bridgeName: string, data?: any): Promise<TResponse> => {
  const requestData = Object.assign({}, getBridgeBaseRequest(), data || {});
  return callBridgeAsync<TResponse>(bridgeName, requestData);
};

/**
 * 안드로이드 브릿지 함수 호출
 * @param bridgeName - AndroidBridge에서 호출할 메서드 명
 * @param requestData - 전달할 데이터
 */
const callBridgeAsync = async <TResponse>(bridgeName: string, requestData?: any): Promise<TResponse> => {
  logger.info(`[Android] ${bridgeName} Request`, requestData);

  // 중복 호출 방지를 위해 각 브릿지별로 고유한 요청 ID 생성
  const requestId = Date.now() + Math.random();
  lastBridgeRequestIds[bridgeName] = requestId;

  return new Promise<TResponse>((resolve, reject) => {
    // 1. 글로벌 콜백 등록 (이미 존재하더라도 새 로직으로 덮어씌움)
    (window as any)[bridgeName] = (res: any) => {
      // 이전 요청의 응답이 오기 전에 새로운 요청이 들어온 경우, 이전 요청의 응답은 무시
      if (lastBridgeRequestIds[bridgeName] !== requestId) return;

      try {
        const response = typeof res === 'string' ? JSON.parse(res) : res;
        logger.info(`[Android] ${bridgeName} Response`, response);

        // 응답을 처리한 후에는 빈 함수로 두어 안드로이드 에러 방지
        // 다음 요청이 오면 다시 이 콜백 전체가 새 resolve를 가진 함수로 덮어씌워집니다.
        (window as any)[bridgeName] = () => {};

        resolve(response);
      } catch (e) {
        reject(new Error(`[Android] ${bridgeName} 파싱 에러`));
      }
    };

    // 2. 안드로이드 브릿지 호출
    if (window.AndroidBridge && (window.AndroidBridge as any)[bridgeName]) {
      if (requestData === undefined) {
        (window.AndroidBridge as any)[bridgeName]();
      } else {
        (window.AndroidBridge as any)[bridgeName](JSON.stringify(requestData));
      }
    } else {
      // 브릿지가 없을 때만 정리
      (window as any)[bridgeName] = () => {};
      reject(new Error(`AndroidBridge.${bridgeName}를 찾을 수 없습니다.`));
    }
  });
};

/**
 * POS API 호출 제네릭 함수
 * 안드로이드 앱 업데이트 없이도 API URL 및 헤더, 바디 등을 동적으로 변경하여 호출 가능.
 * @param callbackName 콜백 함수 이름
 * @param method API 호출 메소드
 * @param url API 호출 URL
 * @param data API 호출 요청 데이터
 * @returns API 호출 응답 데이터
 */
const callPosApi = async <TRequest = any, TResponse = any>(
  callbackName: string,
  method: 'get' | 'post',
  url: string,
  data?: TRequest,
): Promise<ApiResponse<TResponse>> => {
  // 기본 태블릿 설정 값과 전달된 요청 파라미터 병합
  const baseReqData = getBridgeBaseRequest();

  const payload = {
    callbackName,
    params: {
      method,
      url,
      headers: {
        CompanyID: baseReqData.CompanyID,
        StoreCode: baseReqData.StoreCode,
        PosID: baseReqData.PosID,
        Lang: baseReqData.Lang,
      },
      body: data || {},
    },
  };
  const payloadString = JSON.stringify(payload);

  // 로깅
  logger.info(`[Android] ${callbackName} Request: ${payloadString}`);

  try {
    // 2. 브릿지 호출을 Promise로 래핑
    const response = await new Promise<string>((resolve, reject) => {
      (window as any)[callbackName] = (res: string) => {
        resolve(res);
      };

      if (window.AndroidBridge?.callApi) {
        window.AndroidBridge.callApi(payloadString);
      } else {
        reject(new Error('AndroidBridge.callApi를 찾을 수 없습니다.'));
      }
    });

    logger.info(`[Android] ${callbackName} Response: ${response}`);

    // 3. 파싱 및 비즈니스 로직 에러 체크
    const parsed = JSON.parse(response) as ApiResponse<TResponse>;

    if (!parsed || parsed.RET_CODE !== 0) {
      throw new Error(parsed?.RET_MSG || 'API 응답이 성공이 아닙니다.');
    }

    return parsed;
  } catch (error: any) {
    logger.error(`[Android] ${callbackName} 실패: ${error.message}`);
    throw error; // 상위 호출자에게 에러 전달
  } finally {
    // 4. 성공/실패 여부와 상관없이 무조건 콜백 삭제
    delete (window as any)[callbackName];
  }
};

/** 앱 종료 */
export const exit = (): void => {
  try {
    logger.info('[Android] Exit');
    window.AndroidBridge.exit();
  } catch (error: any) {
    logger.error('[Android] Exit', error.stack ?? error.message);
  }
};

/** 앱 재시작 */
export const reStart = (): void => {
  try {
    logger.info('[Android] ReStart');
    window.AndroidBridge.reStart();
  } catch (error: any) {
    logger.error('[Android] ReStart', error.stack ?? error.message);
  }
};

/** UP오더 옵션 저장 */
export const saveOption = (): void => {
  try {
    const configStore = useConfigStore();
    const request = Object.assign({}, getBridgeBaseRequest(), configStore.getAndroidOption());
    logger.info('[Android] SaveOption Request', request);
    window.AndroidBridge.saveOption(JSON.stringify(request));
  } catch (error: any) {
    logger.error('[Android] SaveOption', error.stack ?? error.message);
  }
};

/** 메뉴 조회 완료 알림 */
export const loadComplete = (): void => {
  try {
    logger.info('[Android] LoadComplete');
    window.AndroidBridge.loadComplete();
  } catch (error: any) {
    logger.error('[Android] LoadComplete', error.stack ?? error.message);
  }
};

/**
 * MAC주소 조회
 * @returns MAC주소
 */
export const getMacAddress = (): string => {
  try {
    const address = window.AndroidBridge.getMacAddress();
    logger.info('[Android] GetMacAddress', address);
    return address;
  } catch (error: any) {
    if (!window.AndroidBridge && (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'qa')) {
      const address = 'WEB_BROWSER_TEMP_ADDRESS';
      logger.info('[Android] GetMacAddress', address);
      return address;
    } else {
      throw new UserError(i18n.global.t('error.no_mac'));
    }
  }
};

/** 배너 표시 */
export const showBanner = (): void => {
  try {
    window.AndroidBridge.showBanner();
    logger.info('[Android] ShowBanner');
  } catch (error: any) {
    logger.error('[Android] ShowBanner', error.stack ?? error.message);
  }
};

/**
 * 배너 표시 여부 조회
 * @returns 배너 표시 여부. 에러 발생 시 false.
 */
export const isShowBanner = (): boolean => {
  try {
    const isShowBanner = window.AndroidBridge.isShowBanner();
    logger.info('[Android] IsShowBanner', isShowBanner);
    return isShowBanner;
  } catch (error: any) {
    logger.error('[Android] IsShowBanner', error.stack ?? error.message);
    return false;
  }
};

/** 업데이트 확인 */
export const checkUpdate = (): void => {
  try {
    window.AndroidBridge.checkUpdate();
    logger.info('[Android] CheckUpdate');
  } catch (error: any) {
    logger.error('[Android] CheckUpdate', error.stack ?? error.message);
  }
};

/**
 * 현재 버전 조회
 * @returns 현재 버전. 에러 발생 시 빈 값
 */
export const getVersion = (): string => {
  try {
    const version = window.AndroidBridge.getVersion();
    logger.info('[Android] GetVersion', version);
    return version;
  } catch (error: any) {
    logger.error('[Android] GetVersion', error.stack ?? error.message);
    return '';
  }
};

/**
 * IP주소 조회
 * @returns IP주소. 에러 발생 시 빈 값
 */
export const getIPAddress = (): string => {
  try {
    logger.info('[Android] GetIPAddress');
    const ipAddress = window.AndroidBridge.getIPAddress();
    logger.info('[Android] GetIPAddress', ipAddress);
    return ipAddress;
  } catch (error: any) {
    logger.error('[Android] GetIPAddress', error.stack ?? error.message);
    return '';
  }
};

/**
 * 안드로이드 직원 호출 요청 메소드를 호출
 * @param request 직원 호출 데이터
 */
export const customerCallAsync = async (request: CustomerCallRequest): Promise<BridgeBaseResponse> =>
  callBridgeWithBaseAsync<BridgeBaseResponse>('customerCall', request);

/** 주문 목록 조회 (주문 목록 페이지에서 사용. 주문 유지 옵션에 따라 이전 주문 목록이 조회 됨) */
export const getOrderDataAsync = async (): Promise<GetOrderDataResponse> =>
  callBridgeWithBaseAsync<GetOrderDataResponse>('getOrderData');

/** 주문 조회 (주문하기 전에 사용. 현재 주문만 포함) */
export const getOrderDataForOrderAsync = async (): Promise<GetOrderDataResponse> =>
  callBridgeWithBaseAsync<GetOrderDataResponse>('getOrderDataForOrder');

/**
 * 주문 추가 요청
 * @param request 주문 전송 요청 데이터
 */
export const setOrderDataAsync = async (request: SetOrderDataRequest): Promise<SetOrderDataResponse> =>
  callBridgeWithBaseAsync<SetOrderDataResponse>('setOrderData', request);

/**
 * 카드 결제 요청
 * @param request 카드 결제 요청 데이터
 */
export const approvalCreditCardAsync = async (request: PaymentApprovalRequest): Promise<PaymentApprovalResponse> =>
  callBridgeWithBaseAsync<PaymentApprovalResponse>('approvalCreditCard', request);

/**
 * 간편 결제 승인 요청
 * @param request 간편 결제 승인 요청 데이터
 * @returns 간편 결제 승인 응답
 */
export const approvalEasyPayAsync = async (request: PaymentApprovalRequest): Promise<PaymentApprovalResponse> =>
  callBridgeWithBaseAsync<PaymentApprovalResponse>('approvalEasyPay', request);

/** QR코드로 로그인 */
export const getQRLoginAsync = async (): Promise<QrLoginResponse> =>
  callBridgeWithBaseAsync<QrLoginResponse>('getQRLogin');

/**
 * 영수증 출력
 * @param IsReprint 재출력 여부. true면 전체 주문 영수증 출력 요청을 위한 파라미터 추가.
 */
export const printReceiptAsync = async (IsReprint: boolean): Promise<BridgeBaseResponse> =>
  callBridgeWithBaseAsync<BridgeBaseResponse>('printReceipt', { IsReprint });

/**
 * 전자영수증 요청
 * @param CustomerNumber 알림톡 발송 전화번호
 */
export const requestEReceiptAsync = async (CustomerNumber: string): Promise<BridgeBaseResponse> =>
  callBridgeWithBaseAsync<BridgeBaseResponse>('requestEReceipt', { CustomerNumber });

/** 원격 프로그램 설치 및 실행 */
export const installRemoteSupport = (): void => {
  logger.info('[Android] InstallRemoteSupport');
  window.AndroidBridge.installRemoteSupport();
};

/** 결제 모듈 설치 및 실행 */
export const installVANAgent = (): void => {
  try {
    logger.info('[Android] InstallVANAgent');
    window.AndroidBridge.installVANAgent();
  } catch (error: any) {
    throw new UserError(i18n.global.t('error.app_update'));
  }
};

/** 한정수량 조회 (POS에서 호출) */
export const getOrderLimitItemsAsync = async (): Promise<LimitMenuResponse | undefined> => {
  try {
    logger.info('[Android] getOrderLimitItemsAsync Request');

    const response = await new Promise<LimitMenuResponse>((resolve) => {
      window.getOrderLimitItems = resolve;
      window.AndroidBridge.getOrderLimitItems();
    });

    logger.info('[Android] getOrderLimitItemsAsync Response', response);
    return response;
  } catch (error: any) {
    logger.error('[Android] getOrderLimitItemsAsync', error.stack ?? error.message);
  }
};

/** 한정수량 조회 (UP오더에서 사용) */
export const getOrderLimitItemsForOrderAsync = async (): Promise<LimitMenuResponse | undefined> => {
  try {
    return callBridgeAsync<LimitMenuResponse>('getOrderLimitItemsForOrder');
  } catch {
    return undefined;
  }
};

/**
 * 앱트리 광고 요청
 * @returns 광고 리스트
 */
export const getAppTreeAdAsync = async (): Promise<AppTreeAdResponse[]> => {
  try {
    return callBridgeAsync<AppTreeAdResponse[]>('appTreeAdRequest');
  } catch {
    return [];
  }
};

/**
 * 앱트리 광고 로깅
 * @param request 앱트리 광고 로그 요청
 */
export const appTreeAdLog = (request: AppTreeAdLogRequest): void => {
  try {
    logger.info('[Android] AppTreeAdLog Request', request);
    window.AndroidBridge.appTreeAdLog(JSON.stringify(request));
  } catch (error: any) {
    logger.error('[Android] AppTreeAdLog', error.stack ?? error.message);
  }
};

/**
 * CCTV 표시
 * @param ipAddress CCTV IP 주소
 * @param channelCount 표시 채널 수
 */
export const showCCTV = (ipAddress: string, channelCount?: number): void => {
  try {
    logger.info('[Android] ShowCCTV Request', ipAddress, channelCount);
    window.AndroidBridge.showCCTV(ipAddress, channelCount);
  } catch (error: any) {
    logger.error('[Android] ShowCCTV', error.stack ?? error.message);
    throw new UserError(i18n.global.t('error.app_update'));
  }
};

/** 패스트오더 앱으로 돌아가기 */
export const openServiceApp = (): void => {
  try {
    logger.info('[Android] OpenServiceApp Request');
    window.AndroidBridge.openServiceApp();
  } catch (error: any) {
    logger.error('[Android] OpenServiceApp', error.stack ?? error.message);
  }
};

/** 로봇 호출 완료 코드 전달 */
export const completeRobot = (code: string): void => {
  try {
    logger.info('[Android] CompleteRobot Request', code);
    window.AndroidBridge.completeRobot(code);
  } catch (error: any) {
    logger.error('[Android] CompleteRobot', error.stack ?? error.message);
  }
};

/**
 * 서비스 주문(패스트오더 호출 메시지) 전송
 * @param request 서비스 주문 데이터
 */
export const postAddServiceOrderAsync = async (request: ServiceOrderRequest): Promise<ApiResponse> =>
  callPosApi<ServiceOrderRequest>('postAddServiceOrder', 'post', '/api/Order/PostAddServiceOrder', request);

/**
 * 기기상태 조회
 * @returns 기기 상태
 */
export const getDeviceStatus = (): DeviceStatusResponse | null => {
  try {
    //logger.info('[Android] getDeviceStatus Request');
    const response = window.AndroidBridge.getDeviceStatus();
    //logger.info('[Android] getDeviceStatus Response', response);
    return JSON.parse(response) as DeviceStatusResponse;
  } catch (error: any) {
    logger.error('[Android] getDeviceStatus', error.stack ?? error.message);
    return null;
  }
};
